import React, { useEffect, useState, useRef } from 'react';
import { FileText, Download, Mail, ExternalLink, Upload } from 'lucide-react';
import { PageHeader } from './MassTimes';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { AddButton, EditButton, DeleteButton } from '../components/AdminControls';
import EditDialog from '../components/EditDialog';
import ConfirmDialog from '../components/ConfirmDialog';

const BULLETIN_FIELDS = [
  { name: 'date', label: 'Date', required: true, placeholder: 'August 3, 2026' },
  { name: 'title', label: 'Title', required: true, placeholder: '18th Sunday in Ordinary Time' },
  { name: 'size', label: 'File Size', placeholder: '820 KB' },
  { name: 'pdf_url', label: 'External PDF URL (optional)', placeholder: 'Leave blank if you uploaded a file' },
];

const UploadDialog = ({ open, onClose, onSubmit }) => {
  const [form, setForm] = useState({ date: '', title: '' });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setForm({ date: '', title: '' });
      setFile(null);
      setError('');
    }
  }, [open]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { setError('Please choose a PDF file.'); return; }
    setSubmitting(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('date', form.date);
      fd.append('title', form.title);
      await onSubmit(fd);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Upload failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white border border-[#e9e0cc] w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e9e0cc]">
          <h3 className="font-display text-2xl text-[#2a1a45]">Upload Bulletin PDF</h3>
          <button onClick={onClose} className="text-[#5a534a] hover:text-[#3a2359] text-2xl leading-none">×</button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {error && <div className="text-sm text-[#7a1c1c] bg-[#fdecec] border border-[#f0c4c4] p-3">{error}</div>}
          <div>
            <label className="block text-xs tracking-wider uppercase text-[#5a534a] mb-2">Date <span className="text-[#a68a5b]">*</span></label>
            <input required placeholder="August 3, 2026" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="w-full px-4 py-2.5 border border-[#e9e0cc] bg-[#faf7f0] focus:border-[#3a2359] focus:outline-none text-sm" />
          </div>
          <div>
            <label className="block text-xs tracking-wider uppercase text-[#5a534a] mb-2">Title <span className="text-[#a68a5b]">*</span></label>
            <input required placeholder="18th Sunday in Ordinary Time" value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="w-full px-4 py-2.5 border border-[#e9e0cc] bg-[#faf7f0] focus:border-[#3a2359] focus:outline-none text-sm" />
          </div>
          <div>
            <label className="block text-xs tracking-wider uppercase text-[#5a534a] mb-2">PDF File <span className="text-[#a68a5b]">*</span></label>
            <input
              ref={inputRef}
              type="file"
              accept="application/pdf,.pdf"
              onChange={e => setFile(e.target.files?.[0] || null)}
              className="hidden"
            />
            <button type="button" onClick={() => inputRef.current?.click()} className="w-full flex items-center gap-3 px-4 py-3 border-2 border-dashed border-[#e9e0cc] hover:border-[#3a2359] text-left text-sm">
              <Upload size={18} className="text-[#3a2359]" />
              <span className={file ? 'text-[#2a1a45]' : 'text-[#78716c]'}>
                {file ? `${file.name} (${(file.size / 1024).toFixed(0)} KB)` : 'Choose PDF file to upload'}
              </span>
            </button>
          </div>
          <div className="flex items-center justify-end gap-2 pt-3 border-t border-[#e9e0cc]">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-[#e9e0cc] text-sm text-[#5a534a] hover:bg-[#faf7f0]">Cancel</button>
            <button type="submit" disabled={submitting} className="px-5 py-2 bg-[#3a2359] text-white text-sm hover:bg-[#4d2f75] disabled:opacity-60">
              {submitting ? 'Uploading…' : 'Upload'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Bulletin = () => {
  const { toast } = useToast();
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.listBulletins();
      setItems(data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleDownload = (b) => {
    // Prefer uploaded file (via backend), then external URL
    if (b.pdf_filename) {
      window.open(api.bulletinDownloadUrl(b.id), '_blank', 'noopener');
    } else if (b.pdf_url) {
      window.open(b.pdf_url, '_blank', 'noopener');
    } else {
      toast({ title: 'No file available', description: 'This bulletin has no attached file yet.' });
    }
  };

  const handleSave = async (values) => {
    if (editing === 'new') {
      const { data } = await api.createBulletin(values);
      setItems([data, ...items]);
      toast({ title: 'Bulletin added' });
    } else {
      const { data } = await api.updateBulletin(editing.id, values);
      setItems(items.map(it => it.id === data.id ? data : it));
      toast({ title: 'Bulletin updated' });
    }
  };

  const handleUpload = async (fd) => {
    const { data } = await api.uploadBulletin(fd);
    setItems([data, ...items]);
    toast({ title: 'Bulletin uploaded', description: `${data.title} (${data.size})` });
  };

  const handleDelete = async () => {
    await api.deleteBulletin(deleteTarget.id);
    setItems(items.filter(x => x.id !== deleteTarget.id));
    toast({ title: 'Bulletin deleted' });
    setDeleteTarget(null);
  };

  return (
    <>
      <PageHeader
        eyebrow="Weekly Updates"
        title="Parish Bulletin"
        subtitle="Read the latest news, upcoming events, and reflections from All Saints Catholic Parish."
      />

      <section className="max-w-4xl mx-auto px-6 py-16">
        {isAdmin && (
          <div className="mb-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setUploadOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#3a2359] text-white text-xs uppercase tracking-wider hover:bg-[#4d2f75]"
            >
              <Upload size={14} /> Upload PDF
            </button>
            <AddButton onClick={() => setEditing('new')} label="Add Link" />
          </div>
        )}

        {loading ? (
          <p className="text-center text-[#78716c] py-10">Loading bulletins…</p>
        ) : items.length === 0 ? (
          <p className="text-center text-[#78716c] py-10">No bulletins yet.</p>
        ) : (
          <div className="bg-white border border-[#e9e0cc] divide-y divide-[#f2ead6]">
            {items.map((b, idx) => (
              <div key={b.id} className={`flex items-center justify-between gap-4 p-6 hover:bg-[#faf7f0] ${idx === 0 ? 'bg-[#f2ead6]/40' : ''}`}>
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-full bg-[#f2ead6] flex items-center justify-center text-[#3a2359] flex-shrink-0">
                    <FileText size={18} />
                  </div>
                  <div>
                    <p className="text-xs tracking-[0.15em] uppercase text-[#a68a5b] font-medium mb-1">
                      {b.date} {idx === 0 && <span className="ml-2 px-2 py-0.5 bg-[#3a2359] text-white text-[10px] rounded-sm normal-case tracking-normal">Latest</span>}
                    </p>
                    <h3 className="font-display text-xl text-[#2a1a45]">{b.title}</h3>
                    <p className="text-xs text-[#78716c] mt-1">
                      {b.pdf_filename ? 'PDF' : b.pdf_url ? 'External link' : 'No file'}
                      {b.size ? ` — ${b.size}` : ''}
                      {b.pdf_url && !b.pdf_filename && <span className="ml-2 inline-flex items-center gap-1 text-[#3a2359]"><ExternalLink size={11}/> link</span>}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isAdmin && (
                    <>
                      <EditButton onClick={() => setEditing(b)} />
                      <DeleteButton onClick={() => setDeleteTarget(b)} />
                    </>
                  )}
                  <button
                    onClick={() => handleDownload(b)}
                    disabled={!b.pdf_filename && !b.pdf_url}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-[#3a2359] text-[#3a2359] text-sm hover:bg-[#3a2359] hover:text-white whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Download size={14} /> <span className="hidden sm:inline">Download</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-14 bg-[#f2ead6]/60 border border-[#e9e0cc] p-8 md:p-10 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white text-[#3a2359] mb-4"><Mail size={20} /></div>
          <h3 className="font-display text-2xl md:text-3xl text-[#2a1a45] mb-3">Get the Bulletin in Your Inbox</h3>
          <p className="text-[#5a534a] max-w-lg mx-auto mb-5 text-sm">
            Sign up to receive the weekly bulletin and parish news directly by email.
          </p>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              const form = e.currentTarget;
              const email = form.email.value.trim();
              const website = form.website?.value || '';  // honeypot
              if (!email) return;
              try {
                await api.subscribeToBulletin(email, website);
                toast({ title: 'Subscribed', description: `Thank you — we'll email the weekly bulletin to ${email}.` });
                form.reset();
              } catch (err) {
                toast({
                  title: 'Subscribe failed',
                  description: err?.response?.data?.detail || 'Please try again in a moment.',
                  variant: 'destructive',
                });
              }
            }}
            className="flex flex-col sm:flex-row gap-2 max-w-md mx-auto"
            data-testid="bulletin-subscribe-form"
          >
            {/* Honeypot */}
            <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}>
              <label htmlFor="bulletin_website_hp">Website</label>
              <input type="text" id="bulletin_website_hp" name="website" tabIndex={-1} autoComplete="off" defaultValue="" />
            </div>
            <input
              type="email"
              name="email"
              required
              placeholder="you@example.com"
              className="flex-1 px-4 py-3 bg-white border border-[#e9e0cc] focus:border-[#3a2359] focus:outline-none text-sm"
              data-testid="bulletin-subscribe-email"
            />
            <button
              type="submit"
              className="px-5 py-3 bg-[#3a2359] text-white text-sm hover:bg-[#4d2f75]"
              data-testid="bulletin-subscribe-btn"
            >
              Subscribe
            </button>
          </form>
        </div>
      </section>

      <EditDialog
        open={!!editing}
        title={editing === 'new' ? 'Add Bulletin (external link)' : 'Edit Bulletin'}
        fields={BULLETIN_FIELDS}
        initial={editing === 'new' ? { date: '', title: '', size: '', pdf_url: '' } : (editing || {})}
        onClose={() => setEditing(null)}
        onSubmit={handleSave}
      />
      <UploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} onSubmit={handleUpload} />
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete this bulletin?"
        message={deleteTarget ? `“${deleteTarget.title}” will be permanently removed${deleteTarget.pdf_filename ? ' along with its uploaded PDF' : ''}.` : ''}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </>
  );
};

export default Bulletin;
